import * as React from "react";
import { ColumnType, BrowserLink, BrowserData } from "../types";

interface UseKeyboardNavigationProps {
  activeColumn: ColumnType;
  visibleColumns: ColumnType[];
  getFilteredItems: (column: ColumnType) => (BrowserLink | BrowserData)[];
  getFocusedIndex: (column: ColumnType) => number;
  setActiveColumn: (column: ColumnType) => void;
  setFocusedIndex: (column: ColumnType, index: number) => void;
  onSelectItem: (column: ColumnType, item: BrowserLink | BrowserData) => void;
  columnRefs: React.MutableRefObject<Map<ColumnType, HTMLDivElement | null>>;
  searchInputRefs: React.MutableRefObject<Map<ColumnType, HTMLInputElement | null>>;
}

export function useKeyboardNavigation({
  activeColumn,
  visibleColumns,
  getFilteredItems,
  getFocusedIndex,
  setActiveColumn,
  setFocusedIndex,
  onSelectItem,
  columnRefs,
  searchInputRefs,
}: UseKeyboardNavigationProps) {
  // Track if we should scroll (only on keyboard navigation)
  const shouldScrollRef = React.useRef(false);

  // Scroll focused item into view (only when triggered by keyboard)
  const scrollToFocusedItem = React.useCallback(
    (column: ColumnType, index: number) => {
      const columnRef = columnRefs.current.get(column);
      if (columnRef) {
        const items = columnRef.querySelectorAll("[data-browser-item]");
        const focusedItem = items[index] as HTMLElement;
        if (focusedItem) {
          focusedItem.scrollIntoView({ block: "nearest", behavior: "smooth" });
        }
      }
    },
    [columnRefs]
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      const filteredItems = getFilteredItems(activeColumn);
      const currentIndex = getFocusedIndex(activeColumn);
      const currentColumnIndex = visibleColumns.indexOf(activeColumn);

      // Check if we're in a search input
      const isInSearchInput = (e.target as HTMLElement).tagName === "INPUT";

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          if (filteredItems.length > 0) {
            // If no focus yet, start at 0; otherwise move down
            const newIndex = currentIndex < 0 ? 0 : Math.min(currentIndex + 1, filteredItems.length - 1);
            if (newIndex !== currentIndex) {
              setFocusedIndex(activeColumn, newIndex);
              scrollToFocusedItem(activeColumn, newIndex);
            }
          }
          break;

        case "ArrowUp":
          e.preventDefault();
          if (filteredItems.length > 0) {
            // If no focus yet, start at last item; otherwise move up
            const newIndex = currentIndex < 0 ? filteredItems.length - 1 : Math.max(currentIndex - 1, 0);
            if (newIndex !== currentIndex) {
              setFocusedIndex(activeColumn, newIndex);
              scrollToFocusedItem(activeColumn, newIndex);
            }
          }
          break;

        case "ArrowRight":
          if (!isInSearchInput) {
            e.preventDefault();
            if (currentColumnIndex < visibleColumns.length - 1) {
              const nextColumn = visibleColumns[currentColumnIndex + 1];
              setActiveColumn(nextColumn);
              columnRefs.current.get(nextColumn)?.focus();
            }
          }
          break;

        case "ArrowLeft":
          if (!isInSearchInput) {
            e.preventDefault();
            if (currentColumnIndex > 0) {
              const prevColumn = visibleColumns[currentColumnIndex - 1];
              setActiveColumn(prevColumn);
              columnRefs.current.get(prevColumn)?.focus();
            }
          }
          break;

        case "Enter":
        case " ":
          if (!isInSearchInput) {
            e.preventDefault();
            // Only select if we have a valid focused index
            if (currentIndex >= 0 && currentIndex < filteredItems.length) {
              const selectedItem = filteredItems[currentIndex];
              onSelectItem(activeColumn, selectedItem);
            }
          }
          break;

        case "Home":
          if (!isInSearchInput) {
            e.preventDefault();
            setFocusedIndex(activeColumn, 0);
            scrollToFocusedItem(activeColumn, 0);
          }
          break;

        case "End":
          if (!isInSearchInput) {
            e.preventDefault();
            const lastIndex = filteredItems.length - 1;
            setFocusedIndex(activeColumn, lastIndex);
            scrollToFocusedItem(activeColumn, lastIndex);
          }
          break;

        case "/":
          if (!isInSearchInput) {
            e.preventDefault();
            // Focus search input of current column
            const searchInput = searchInputRefs.current.get(activeColumn);
            searchInput?.focus();
          }
          break;

        case "Escape":
          if (isInSearchInput) {
            // Blur search input and focus column
            (e.target as HTMLInputElement).blur();
            columnRefs.current.get(activeColumn)?.focus();
          }
          // Let the modal handle escape for closing
          break;
      }
    },
    [
      activeColumn,
      visibleColumns,
      getFilteredItems,
      getFocusedIndex,
      setActiveColumn,
      setFocusedIndex,
      onSelectItem,
      columnRefs,
      searchInputRefs,
      scrollToFocusedItem,
    ]
  );

  return {
    handleKeyDown,
  };
}
